import type { AuditLogRow } from '@shared/types/api';

export type ChangeStatus = 'granted' | 'revoked' | 'added' | 'removed' | 'updated' | 'info';

export interface SemanticChangeItem {
  category?: string;
  label: string;
  before?: string | null;
  after?: string | null;
  status: ChangeStatus;
  description?: string;
}

export interface SemanticEventAnalysis {
  title: string;
  summary: string;
  changes: SemanticChangeItem[];
  noChangesReason?: string;
}

const IGNORED_TECHNICAL_FIELDS = new Set([
  'id',
  'userId',
  'user_id',
  'createdAt',
  'created_at',
  'updatedAt',
  'updated_at',
  'deletedAt',
  'deleted_at',
  '__v',
]);

const SENSITIVE_KEY_REGEX = /(password|hash|token|secret|otp|salt)/i;

/**
 * Returns human-readable role labels
 */
export function formatRoleLabel(role?: string | null): string {
  if (!role) return 'User';
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin';
    case 'OPS_ADMIN':
      return 'Operations Admin';
    case 'READ_ONLY_ADMIN':
      return 'Read-Only Admin';
    case 'ASSIGNER':
      return 'Assigner';
    case 'ASSIGNER_ADMIN':
      return 'Assigner Admin';
    case 'ADMIN':
      return 'Administrator';
    case 'ASSOCIATE':
      return 'Associate Partner';
    case 'CLIENT':
      return 'Client';
    default:
      return role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/**
 * Returns human-readable field label
 */
export function formatFieldLabel(key: string): string {
  const map: Record<string, string> = {
    platformFeePercentage: 'Platform Fee (%)',
    platformFeeFlatPaise: 'Platform Flat Fee',
    paymentGatewayFeeNonRefundable: 'Payment Gateway Fee Non-Refundable',
    supportPeriodDays: 'Support Period (Days)',
    refundProcessingDays: 'Refund Processing (Days)',
    invoiceAutoCancelDays: 'Invoice Auto-Cancel (Days)',
    ecregiLegalName: 'Legal Entity Name',
    ecregiGstin: 'GSTIN',
    ecregiPan: 'PAN Number',
    ecregiState: 'State',
    ecregiRegisteredAddress: 'Registered Address',
    defaultGstPercentage: 'Default GST (%)',
    defaultTdsPercentageForPayout: 'Default TDS Rate (%)',
    tdsSectionCode: 'TDS Section Code',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    mobile: 'Mobile Number',
    status: 'Account Status',
    adminRole: 'Admin Role',
    role: 'User Role',
    canView: 'View Permission',
    canCreate: 'Create Permission',
    canEdit: 'Edit Permission',
    canDelete: 'Delete Permission',
    name: 'Name',
    code: 'Code',
    description: 'Description',
    isArchived: 'Archived',
    isActive: 'Active',
    basePriceRupees: 'Base Price (₹)',
    candidateAssociateIds: 'Candidate Associates',
    assignedAssociateId: 'Assigned Associate',
    reason: 'Reason / Note',
    invoiceNumber: 'Invoice Number',
    totalPaise: 'Total Amount (Paise)',
    subtotalPaise: 'Subtotal (Paise)',
    gstPaise: 'GST (Paise)',
    paymentMode: 'Payment Mode',
    offlineReference: 'Offline Reference',
    bankReference: 'Bank Reference',
    finalRefundPaise: 'Refund Amount (Paise)',
    paidAmountPaise: 'Paid Amount (Paise)',
    eligibleRefundPaise: 'Eligible Refund (Paise)',
    providerRefundId: 'Gateway Refund ID',
    rejectionReason: 'Rejection Reason',
    lineItemsCount: 'Line Items Count',
    transactionCode: 'Transaction Code',
  };

  return map[key] || key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

/**
 * Formats an individual item (scalar or object) into a clean, human-readable summary.
 * Avoids any "[object Object]" leaks by extracting entity names, codes, labels, or titles.
 */
export function formatObjectSummary(item: unknown, resolvedEntities?: AuditLogRow['resolvedEntities']): string {
  if (item === null || item === undefined) return '';
  if (typeof item !== 'object') {
    if (typeof item === 'string' && resolvedEntities?.[item]) {
      return resolvedEntities[item].label;
    }
    return String(item);
  }

  const obj = item as Record<string, unknown>;

  // If object has an ID that matches a resolved entity from DB
  if (typeof obj.id === 'string' && resolvedEntities?.[obj.id]) {
    return resolvedEntities[obj.id].label;
  }

  // Common descriptive properties on models (Service, Authority, Document, User, etc.)
  if (obj.name && obj.code) {
    return `${obj.name} (${obj.code})`;
  }
  if (obj.name) return String(obj.name);
  if (obj.title) return String(obj.title);
  if (obj.label) return String(obj.label);
  if (obj.code) return String(obj.code);
  if (obj.pincode) return String(obj.pincode);
  if (obj.email) {
    const fullName = [obj.firstName, obj.lastName].filter(Boolean).join(' ');
    return fullName ? `${fullName} (${obj.email})` : String(obj.email);
  }

  // Filter out internal technical noisy fields and format key-value pairs
  const meaningfulEntries = Object.entries(obj).filter(([k, v]) => {
    if (IGNORED_TECHNICAL_FIELDS.has(k)) return false;
    if (SENSITIVE_KEY_REGEX.test(k)) return false;
    if (k.toLowerCase().endsWith('id')) return false;
    return v !== null && v !== undefined && typeof v !== 'object';
  });

  if (meaningfulEntries.length > 0) {
    return meaningfulEntries.map(([k, v]) => `${formatFieldLabel(k)}: ${v}`).join(', ');
  }

  return JSON.stringify(obj);
}

/**
 * Format primitive or nested value safely for human presentation with field-aware semantics.
 * Booleans on permissions -> Allowed/Denied. Booleans on active/archive -> Active/Inactive, Archived/Active.
 * General booleans -> Yes/No. Deletion/missing fields -> clear labels like 'None', 'No role assigned'.
 */
export function formatHumanValue(
  val: unknown,
  resolvedEntities?: AuditLogRow['resolvedEntities'],
  fieldKey?: string,
): string {
  if (val === undefined || val === null) {
    if (fieldKey === 'adminRole' || fieldKey === 'role') return 'No role assigned';
    if (fieldKey === 'deletedAt') return 'Not deleted';
    return 'None';
  }

  if (typeof val === 'boolean') {
    // Permission flags
    if (fieldKey && (fieldKey.startsWith('can') || fieldKey.includes('Permission') || fieldKey === 'permission')) {
      return val ? 'Allowed' : 'Denied';
    }
    // Activation / Archive flags
    if (fieldKey === 'isActive') return val ? 'Active' : 'Inactive';
    if (fieldKey === 'isArchived') return val ? 'Archived' : 'Active';
    if (fieldKey === 'paymentGatewayFeeNonRefundable') return val ? 'Non-refundable' : 'Refundable';
    // General booleans
    return val ? 'Yes' : 'No';
  }

  if (typeof val === 'string') {
    if (resolvedEntities?.[val]) {
      const info = resolvedEntities[val];
      return info.subtext ? `${info.label} (${info.subtext})` : info.label;
    }
    if (fieldKey === 'role' || fieldKey === 'adminRole') {
      return formatRoleLabel(val);
    }
    if (fieldKey === 'status') {
      return val.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    }
    return val;
  }

  if (Array.isArray(val)) {
    if (val.length === 0) return 'None';
    return val
      .map((item) => formatObjectSummary(item, resolvedEntities))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof val === 'object') {
    return formatObjectSummary(val, resolvedEntities);
  }

  return String(val);
}

/**
 * Human-readable action title mapping
 */
export function resolveActionTitle(action: string): string {
  const titles: Record<string, string> = {
    'admin_permissions.updated': 'Permissions Updated',
    'admin.created': 'Admin Account Created',
    'admin.updated': 'Admin Account Updated',
    'admin.deleted': 'Admin Account Deleted',
    'admin.user_approved': 'Admin Account Approved',
    'admin.user_rejected': 'Admin Account Rejected',
    'user.disabled': 'Account Suspended',
    'user.enabled': 'Account Reinstated',
    'user.deleted': 'Account Deleted',
    'user.admin_role_changed': 'Admin Role Changed',
    'transaction.assigned': 'Transaction Assigned',
    'transaction.assignment_revoked': 'Transaction Assignment Revoked',
    'transaction.force_completed': 'Transaction Force Completed',
    'transaction.force_cancelled': 'Transaction Force Cancelled',
    'service.created': 'Service Created',
    'services.reordered': 'Services Reordered',
    'service.updated': 'Service Updated',
    'sub_service.created': 'Sub-Service Created',
    'sub_service.updated': 'Sub-Service Updated',
    'pincodes.upserted': 'Service Pincodes Updated',
    'payout.created': 'Payout Requested',
    'payout.marked_paid': 'Payout Marked Paid',
    'payout.cancelled': 'Payout Cancelled',
    'payout.held': 'Payout Put On Hold',
    'kyc.document.viewed': 'KYC Document Viewed',
    'kyc.approved': 'KYC Approved',
    'kyc.rejected': 'KYC Rejected',
    'platform_config.updated': 'Platform Settings Updated',
    'authority.created': 'Authority Created',
    'authority.updated': 'Authority Updated',
    'authority.deleted': 'Authority Deleted',
    'authority_service.created': 'Authority Service Created',
    'authority_service.updated': 'Authority Service Updated',
    'authority_service.deleted': 'Authority Service Deleted',
    'authorities.bulk_imported': 'Authorities Bulk Imported',
    'invoice.created': 'Invoice Created',
    'invoice.marked_paid_offline': 'Invoice Marked Paid Offline',
    'invoice.cancelled': 'Invoice Cancelled',
    'invoice.sent': 'Invoice Sent',
    'refund.approved': 'Refund Approved',
    'refund.rejected': 'Refund Rejected',
    'refund.marked_offline': 'Refund Marked Offline',
  };

  if (titles[action]) return titles[action];

  // Fallback: convert canonical "entity.action_verb" to "Entity Action Verb"
  const parts = action.split('.');
  const entity = parts[0]?.replace(/_/g, ' ') || 'Record';
  const verb = parts[1]?.replace(/_/g, ' ') || 'Modified';
  return `${entity} ${verb}`.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Human-readable plain English Activity Summary resolver
 */
export function resolveActivitySummary(log: AuditLogRow): string {
  const actorFullName = log.actor
    ? [log.actor.firstName, log.actor.lastName].filter(Boolean).join(' ')
    : '';
  const actorRole = log.actor?.adminRole
    ? formatRoleLabel(log.actor.adminRole)
    : log.actor?.role === 'ADMIN'
      ? 'Administrator'
      : log.actor
        ? formatRoleLabel(log.actor.role)
        : 'System';

  let actorDesc: string;
  if (!log.actor) {
    actorDesc = 'System';
  } else if (actorFullName) {
    actorDesc =
      actorRole && actorRole.toLowerCase() !== actorFullName.toLowerCase()
        ? `${actorRole} (${actorFullName})`
        : actorFullName;
  } else if (actorRole && actorRole !== 'User') {
    actorDesc = actorRole;
  } else {
    actorDesc = log.actor.email || 'Administrator';
  }

  const targetResolved = log.entityId ? log.resolvedEntities?.[log.entityId] : null;
  const targetName =
    targetResolved?.label ||
    (log.entityLabel ? log.entityLabel.replace(/\s*\(.*?\)$/, '') : '') ||
    log.entityType ||
    'record';

  switch (log.action) {
    case 'admin_permissions.updated':
      return `${actorDesc} updated permissions for ${targetName}.`;
    case 'admin.created':
      return `${actorDesc} provisioned an admin account for ${targetName}.`;
    case 'admin.updated':
      return `${actorDesc} updated the admin account for ${targetName}.`;
    case 'admin.deleted': {
      const bSnap = (log.before || {}) as Record<string, unknown>;
      const deletionType = bSnap.deletionType as string | undefined;
      if (deletionType === 'PERMANENT') {
        return `${actorDesc} permanently deleted the admin account for ${targetName}.`;
      }
      return `${actorDesc} deleted the admin account for ${targetName}.`;
    }
    case 'user.admin_role_changed': {
      const snap = (log.after || {}) as Record<string, unknown>;
      const newRole = snap.adminRole ? formatRoleLabel(String(snap.adminRole)) : 'updated role';
      return `${actorDesc} changed ${targetName}'s administrative role to ${newRole}.`;
    }
    case 'admin.user_approved':
      return `${actorDesc} approved the admin account for ${targetName}.`;
    case 'admin.user_rejected':
      return `${actorDesc} rejected the admin account registration for ${targetName}.`;
    case 'user.disabled':
      return `${actorDesc} suspended the account for ${targetName}.`;
    case 'user.enabled':
      return `${actorDesc} reinstated the account for ${targetName}.`;
    case 'user.deleted': {
      const bSnap = (log.before || {}) as Record<string, unknown>;
      const aSnap = (log.after || {}) as Record<string, unknown>;
      const deletionType = (bSnap.deletionType || aSnap.deletionType) as string | undefined;
      const accountDesc = (bSnap.adminRole || bSnap.role === 'ADMIN') ? 'admin account' : 'account';

      if (deletionType === 'PERMANENT') {
        return `${actorDesc} permanently deleted the ${accountDesc} for ${targetName}.`;
      }
      if (deletionType === 'SOFT') {
        return `${actorDesc} soft-deleted the ${accountDesc} for ${targetName}.`;
      }
      if (deletionType === 'DEACTIVATION') {
        return `${actorDesc} deactivated the ${accountDesc} for ${targetName}.`;
      }
      // Neutral fallback: do not claim permanent deletion unless backend confirmed it
      return `${actorDesc} deleted the ${accountDesc} for ${targetName}.`;
    }
    case 'transaction.assigned':
      return `${actorDesc} assigned service request ${targetName} to associate partner(s).`;
    case 'transaction.assignment_revoked':
      return `${actorDesc} revoked the associate partner assignment for ${targetName}.`;
    case 'transaction.force_completed':
      return `${actorDesc} marked transaction ${targetName} as force-completed.`;
    case 'transaction.force_cancelled':
      return `${actorDesc} cancelled transaction ${targetName}.`;
    case 'platform_config.updated':
      return `${actorDesc} updated platform configuration and tax settings.`;
    case 'service.created':
      return `${actorDesc} created catalog service "${targetName}".`;
    case 'service.updated':
      return `${actorDesc} updated catalog service "${targetName}".`;
    case 'services.reordered':
      return `${actorDesc} updated display ordering for catalog services.`;
    case 'authority.created':
      return `${actorDesc} created registration authority "${targetName}".`;
    case 'authority.updated':
      return `${actorDesc} updated registration authority "${targetName}".`;
    case 'authority.deleted':
      return `${actorDesc} deleted registration authority "${targetName}".`;
    case 'kyc.approved':
      return `${actorDesc} approved KYC verification documents for ${targetName}.`;
    case 'kyc.rejected':
      return `${actorDesc} rejected KYC verification documents for ${targetName}.`;
    case 'payout.created':
      return `${actorDesc} created payout request for ${targetName}.`;
    case 'payout.marked_paid':
      return `${actorDesc} marked payout for ${targetName} as completed and paid.`;
    case 'invoice.created':
      return `${actorDesc} generated invoice ${targetName}.`;
    case 'invoice.marked_paid_offline':
      return `${actorDesc} recorded offline payment for invoice ${targetName}.`;
    case 'invoice.cancelled':
      return `${actorDesc} cancelled invoice ${targetName}.`;
    case 'invoice.sent':
      return `${actorDesc} marked invoice ${targetName} as sent.`;
    case 'refund.approved':
      return `${actorDesc} approved refund request for ${targetName}.`;
    case 'refund.rejected':
      return `${actorDesc} rejected refund request for ${targetName}.`;
    case 'refund.marked_offline':
      return `${actorDesc} marked refund for ${targetName} as paid offline.`;
    default:
      return `${actorDesc} performed action "${log.action}" on ${targetName}.`;
  }
}

/**
 * Semantic Permission Diff Generator
 * Compares before and after permission arrays by stable `section` key.
 * Only reports actual changed permissions (e.g. canCreate false -> true: "Granted Create permission for Users").
 */
function analyzePermissionDiff(before: unknown, after: unknown): SemanticChangeItem[] {
  interface PermRow {
    section: string;
    canView?: boolean;
    canCreate?: boolean;
    canEdit?: boolean;
    canDelete?: boolean;
  }

  const extractPerms = (obj: unknown): PermRow[] => {
    if (!obj) return [];
    if (Array.isArray(obj)) return obj as PermRow[];
    if (typeof obj === 'object' && 'permissions' in (obj as Record<string, unknown>)) {
      const p = (obj as Record<string, unknown>).permissions;
      if (Array.isArray(p)) return p as PermRow[];
    }
    return [];
  };

  const beforePerms = extractPerms(before);
  const afterPerms = extractPerms(after);

  const beforeMap = new Map<string, PermRow>();
  for (const p of beforePerms) {
    if (p.section) beforeMap.set(p.section, p);
  }

  const afterMap = new Map<string, PermRow>();
  for (const p of afterPerms) {
    if (p.section) afterMap.set(p.section, p);
  }

  const allSections = Array.from(new Set([...beforeMap.keys(), ...afterMap.keys()])).sort();
  const changes: SemanticChangeItem[] = [];

  const flags: Array<{ key: 'canView' | 'canCreate' | 'canEdit' | 'canDelete'; label: string }> = [
    { key: 'canView', label: 'View' },
    { key: 'canCreate', label: 'Create' },
    { key: 'canEdit', label: 'Edit' },
    { key: 'canDelete', label: 'Delete' },
  ];

  for (const sec of allSections) {
    const b = beforeMap.get(sec);
    const a = afterMap.get(sec);
    const secLabel = sec.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

    // Section added completely
    if (!b && a) {
      for (const flag of flags) {
        if (a[flag.key]) {
          changes.push({
            category: `${secLabel} Permissions`,
            label: `${flag.label} Permission`,
            before: 'Denied',
            after: 'Allowed',
            status: 'granted',
            description: `Granted ${flag.label} permission for ${secLabel}`,
          });
        }
      }
      continue;
    }

    // Section removed completely
    if (b && !a) {
      for (const flag of flags) {
        if (b[flag.key]) {
          changes.push({
            category: `${secLabel} Permissions`,
            label: `${flag.label} Permission`,
            before: 'Allowed',
            after: 'Denied',
            status: 'revoked',
            description: `Revoked ${flag.label} permission for ${secLabel}`,
          });
        }
      }
      continue;
    }

    if (b && a) {
      for (const flag of flags) {
        const bVal = Boolean(b[flag.key]);
        const aVal = Boolean(a[flag.key]);
        if (bVal !== aVal) {
          changes.push({
            category: `${secLabel} Permissions`,
            label: `${flag.label} Permission`,
            before: bVal ? 'Allowed' : 'Denied',
            after: aVal ? 'Allowed' : 'Denied',
            status: aVal ? 'granted' : 'revoked',
            description: aVal
              ? `Granted ${flag.label} permission for ${secLabel}`
              : `Revoked ${flag.label} permission for ${secLabel}`,
          });
        }
      }
    }
  }

  return changes;
}

/**
 * Generic Diff Engine
 * Compares two objects, filters out internal technical noise, and formats readable field differences.
 */
function analyzeGenericDiff(
  before: unknown,
  after: unknown,
  resolvedEntities?: AuditLogRow['resolvedEntities'],
): SemanticChangeItem[] {
  const bObj = before && typeof before === 'object' && !Array.isArray(before) ? (before as Record<string, unknown>) : null;
  const aObj = after && typeof after === 'object' && !Array.isArray(after) ? (after as Record<string, unknown>) : null;

  if (!bObj && !aObj) return [];

  const allKeys = new Set([
    ...(bObj ? Object.keys(bObj) : []),
    ...(aObj ? Object.keys(aObj) : []),
  ]);

  const changes: SemanticChangeItem[] = [];

  for (const key of Array.from(allKeys).sort()) {
    // Ignore internal IDs, technical audit timestamps, and sensitive tokens/hashes
    if (IGNORED_TECHNICAL_FIELDS.has(key)) continue;
    if (SENSITIVE_KEY_REGEX.test(key)) continue;

    const bVal = bObj ? bObj[key] : undefined;
    const aVal = aObj ? aObj[key] : undefined;

    const bStr = JSON.stringify(bVal);
    const aStr = JSON.stringify(aVal);

    if (bStr === aStr) continue;

    const label = formatFieldLabel(key);

    if (bVal === undefined && aVal !== undefined) {
      changes.push({
        label,
        before: 'Not set',
        after: formatHumanValue(aVal, resolvedEntities, key),
        status: 'added',
        description: `Added ${label}: ${formatHumanValue(aVal, resolvedEntities, key)}`,
      });
    } else if (bVal !== undefined && aVal === undefined) {
      changes.push({
        label,
        before: formatHumanValue(bVal, resolvedEntities, key),
        after: 'Removed',
        status: 'removed',
        description: `Removed ${label}`,
      });
    } else {
      changes.push({
        label,
        before: formatHumanValue(bVal, resolvedEntities, key),
        after: formatHumanValue(aVal, resolvedEntities, key),
        status: 'updated',
        description: `Updated ${label} from "${formatHumanValue(bVal, resolvedEntities, key)}" to "${formatHumanValue(aVal, resolvedEntities, key)}"`,
      });
    }
  }

  return changes;
}

/**
 * Master Semantic Diff Analyzer
 * Produces structured human-readable title, summary, and change list for any audit event.
 */
export function analyzeAuditEvent(log: AuditLogRow): SemanticEventAnalysis {
  const title = resolveActionTitle(log.action);
  const summary = resolveActivitySummary(log);

  const targetResolved = log.entityId ? log.resolvedEntities?.[log.entityId] : null;
  const targetName =
    targetResolved?.label ||
    (log.entityLabel ? log.entityLabel.replace(/\s*\(.*?\)$/, '') : '') ||
    log.entityType ||
    'record';

  let changes: SemanticChangeItem[] = [];

  // 1. Permission Changes
  if (log.action === 'admin_permissions.updated') {
    changes = analyzePermissionDiff(log.before, log.after);
  }
  // 2. Account Role Changes
  else if (log.action === 'user.admin_role_changed') {
    const bSnap = (log.before || {}) as Record<string, unknown>;
    const aSnap = (log.after || {}) as Record<string, unknown>;
    const prevRole = bSnap.adminRole ? formatRoleLabel(String(bSnap.adminRole)) : 'No role assigned';
    const nextRole = aSnap.adminRole ? formatRoleLabel(String(aSnap.adminRole)) : 'No role assigned';
    changes.push({
      category: 'Role Assignment',
      label: 'Admin Role',
      before: prevRole,
      after: nextRole,
      status: 'updated',
      description: `Role changed from ${prevRole} to ${nextRole}`,
    });
  }
  // 3. Account Disabled / Enabled
  else if (log.action === 'user.disabled') {
    const aSnap = (log.after || {}) as Record<string, unknown>;
    changes.push({
      category: 'Account Status',
      label: 'Status',
      before: 'Active',
      after: 'Suspended',
      status: 'revoked',
      description: 'Account access suspended',
    });
    if (aSnap.reason) {
      changes.push({
        category: 'Account Status',
        label: 'Suspension Reason',
        before: 'None',
        after: String(aSnap.reason),
        status: 'info',
      });
    }
  } else if (log.action === 'user.enabled') {
    changes.push({
      category: 'Account Status',
      label: 'Status',
      before: 'Suspended',
      after: 'Active',
      status: 'granted',
      description: 'Account access reinstated',
    });
  }
  // 4. Admin Account Provisioned
  else if (log.action === 'admin.created') {
    const aSnap = (log.after || {}) as Record<string, unknown>;
    changes.push({
      category: 'Account Details',
      label: 'Admin Account',
      before: 'Not set',
      after: targetName,
      status: 'added',
      description: 'Admin account provisioned',
    });
    if (aSnap.adminRole) {
      changes.push({
        category: 'Role Assignment',
        label: 'Admin Role',
        before: 'No role assigned',
        after: formatRoleLabel(String(aSnap.adminRole)),
        status: 'granted',
      });
    }
    if (aSnap.status) {
      changes.push({
        category: 'Account Details',
        label: 'Account Status',
        before: 'None',
        after: formatHumanValue(aSnap.status, undefined, 'status'),
        status: 'granted',
      });
    }
  }
  // 5. Account Deletion (User or Admin)
  else if (log.action === 'user.deleted' || log.action === 'admin.deleted') {
    const bSnap = (log.before || {}) as Record<string, unknown>;
    const targetFullName = [bSnap.firstName, bSnap.lastName].filter(Boolean).join(' ').trim();
    const targetEmail = typeof bSnap.email === 'string' ? bSnap.email : '';
    let accountIdentity: string;
    if (targetFullName && targetEmail) {
      accountIdentity = `${targetFullName} (${targetEmail})`;
    } else if (targetFullName) {
      accountIdentity = targetFullName;
    } else if (targetEmail) {
      accountIdentity = targetEmail;
    } else {
      accountIdentity = log.entityId ? `#${log.entityId.slice(0, 8)}...` : 'Account';
    }

    changes.push({
      category: 'Account Details',
      label: 'Deleted Account',
      before: accountIdentity,
      after: 'Account deleted',
      status: 'removed',
      description: bSnap.deletionType === 'PERMANENT'
        ? 'Account permanently deleted from system'
        : 'Account deleted from system',
    });

    if (bSnap.role || bSnap.adminRole) {
      changes.push({
        category: 'Account Details',
        label: 'Role at Deletion',
        before: formatRoleLabel(String(bSnap.adminRole || bSnap.role)),
        after: 'No active account',
        status: 'removed',
      });
    }

    if (bSnap.status) {
      changes.push({
        category: 'Account Details',
        label: 'Account Status',
        before: formatHumanValue(bSnap.status, undefined, 'status'),
        after: 'Account deleted',
        status: 'removed',
      });
    }

    if (bSnap.deletionType) {
      const typeLabel =
        bSnap.deletionType === 'PERMANENT'
          ? 'Permanent deletion'
          : bSnap.deletionType === 'SOFT'
            ? 'Soft deletion'
            : bSnap.deletionType === 'DEACTIVATION'
              ? 'Account deactivation'
              : String(bSnap.deletionType);

      changes.push({
        category: 'Account Details',
        label: 'Deletion Type',
        before: 'Active account',
        after: typeLabel,
        status: 'removed',
      });
    }
  }
  // 5. Admin Account Approval / Rejection
  else if (log.action === 'admin.user_approved') {
    const aSnap = (log.after || {}) as Record<string, unknown>;
    changes.push({
      category: 'Account Verification',
      label: 'Account Status',
      before: 'Pending Approval',
      after: 'Active',
      status: 'granted',
      description: 'Administrator account approved',
    });
    if (aSnap.adminRole) {
      changes.push({
        category: 'Role Assignment',
        label: 'Admin Role',
        before: 'No role assigned',
        after: formatRoleLabel(String(aSnap.adminRole)),
        status: 'granted',
      });
    }
  } else if (log.action === 'admin.user_rejected') {
    const aSnap = (log.after || {}) as Record<string, unknown>;
    changes.push({
      category: 'Account Verification',
      label: 'Account Status',
      before: 'Pending Approval',
      after: 'Rejected',
      status: 'revoked',
      description: 'Administrator account rejected',
    });
    if (aSnap.rejectionReason) {
      changes.push({
        category: 'Account Verification',
        label: 'Rejection Reason',
        before: 'None',
        after: String(aSnap.rejectionReason),
        status: 'info',
      });
    }
  }
  // 6. Transaction Assignment
  else if (log.action === 'transaction.assigned') {
    const aSnap = (log.after || {}) as Record<string, unknown>;
    const candidateIds = Array.isArray(aSnap.candidateAssociateIds) ? aSnap.candidateAssociateIds : [];
    const associateNames = candidateIds.map((id) => {
      const info = log.resolvedEntities?.[id];
      return info ? `${info.label} (${info.subtext || 'Associate'})` : id;
    });

    changes.push({
      category: 'Associate Assignment',
      label: 'Candidate Associates',
      before: 'None',
      after: associateNames.length > 0 ? associateNames.join(', ') : 'None',
      status: 'added',
      description: `Offered assignment to ${associateNames.length} associate(s)`,
    });
    if (aSnap.reason) {
      changes.push({
        category: 'Associate Assignment',
        label: 'Assignment Reason',
        before: '<none>',
        after: String(aSnap.reason),
        status: 'info',
      });
    }
  }
  // 6. Generic Fallback
  else {
    changes = analyzeGenericDiff(log.before, log.after, log.resolvedEntities);
  }

  return {
    title,
    summary,
    changes,
    noChangesReason:
      changes.length === 0
        ? 'No field-level changes are available for this event.'
        : undefined,
  };
}
